import isFunction from 'lodash-es/isFunction';
import { TRequireOnlyOne } from '../types';
import { globalFocus } from './globalFocus';
import { screenLocker } from '../screenLock';
import { customEvents, focusSelector } from '../dom';
import { persistentStorage } from '../storage';
import { noNaN } from '../number';

interface IFocusProps {
  modalsContainerRetriever?: () => HTMLElement;
  notificationsContainerRetriever?: () => HTMLElement;
}

export interface IOnFocusConfiguration {
  /**
   * El controlador emite un evento custom cuando se realiza foco, con la
   * intención de que los elementos colapsables se abran automáticamente. Si
   * este parámetro es pasado con false, se evita ese comportamiento.
   */
  dispatchCustomEvent?: boolean;
  /**
   * Cuando la pantalla está bloqueada con el spinner, el controlador no
   * intentará hacer foco, a menos que este parámetro se pase en true.
   */
  focusEvenWhenScreenLocked?: boolean;
  scrollIntoViewOptions?: ScrollIntoViewOptions;
}

export const notificationsSelector = '.notification';

/**
 * Al método focus.on se le puede pasar o bien un HTMLElement o bien una
 * función que implemente esta interfaz. Cuando se pasa una función, la misma
 * será invocada cada vez que se vaya a intentar una acción de foco.
 *
 * Si la función devuelve un elemento HTML, se intentará hacer foco sobre éste.
 *
 * Si la función devuelve null, ese intento será tomado como nulo y se volverá
 * a intentar luego del tiempo determinado.
 *
 * Si la función devuelve false, será eliminada de la cola de foco, lo que
 * significa que ya no se intentará hacer foco sobre este elemento. En cambio,
 * se continuará con los demás elementos de la cola o en caso de no haber
 * ninguno, con el último elemento que tuvo foco y aún esté presente en el
 * documento.
 */
export type TFocusRetriever = (
  isLastTry: boolean,
) => HTMLElement | null | false;

interface IFocusQueryElement {
  element: HTMLElement | TFocusRetriever;
  configuration?: IOnFocusConfiguration;
}

declare global {
  interface Window {
    focusStatus: Record<string, unknown>;
  }
}

interface IFocusCheck {
  currentInstruction: number;
}

export const focus = new (class FocusController {
  #root: HTMLElement;

  #props: IFocusProps | undefined;

  constructor(root: HTMLElement, props?: IFocusProps) {
    this.#root = root;
    this.#props = props;

    window.focusStatus = {};
    globalFocus.onFocus(() => {
      this.#resetInterval();
    });
  }

  afterNotificationFocus: IFocusQueryElement | undefined = undefined;

  #actualFocusQuery: IFocusQueryElement[] = [];

  #currentInstruction = 0;

  #focusDelay = 300;

  #focusRetries = 3;

  #focusTimeout = -1;

  #isIntervalRunning = false;

  set #focusQuery(value: IFocusQueryElement[]) {
    this.#actualFocusQuery = value;
  }

  get #focusQuery() {
    return new Proxy(this.#actualFocusQuery, {
      get: (target, key) => {
        if (key in target) {
          const prop = target[key as keyof typeof target];
          if (isFunction(prop))
            return (...props: unknown[]) => {
              const result = (
                target[key as keyof typeof target] as unknown as (
                  ...methodProps: unknown[]
                ) => void
              ).bind(target, ...props)();
              return result;
            };
          return prop;
        }
        return undefined;
      },
    });
  }

  #checkInstruction(focusCheck: IFocusCheck) {
    return focusCheck.currentInstruction === this.#currentInstruction;
  }

  async #doFocus(
    HTMLElement: HTMLElement | TFocusRetriever,
    focusCheck: IFocusCheck,
    isLastTry: boolean,
    configuration?: IOnFocusConfiguration,
  ) {
    if (
      screenLocker.isLocked('common') &&
      !configuration?.focusEvenWhenScreenLocked
    ) {
      return null;
    }
    const actualHTMLElement = isFunction(HTMLElement)
      ? HTMLElement(isLastTry)
      : HTMLElement;

    if (actualHTMLElement) {
      if ((actualHTMLElement as HTMLInputElement).disabled) return false;
      actualHTMLElement.focus();
      return new Promise<false | null | HTMLElement>((resolve) => {
        if (configuration?.dispatchCustomEvent !== false) {
          const customFocusEvent = new CustomEvent(customEvents.focus, {
            bubbles: true,
          });
          actualHTMLElement.dispatchEvent(customFocusEvent);
        }

        /**
         * Este setTimeout está colocado aquí para asegurarme de que se
         * propague correctamente el customEvent y los procesos de renderizado
         * asociados puedan ser ejecutados antes de proseguir con el proces de
         * foco.
         */
        setTimeout(() => {
          if (!this.#checkInstruction(focusCheck)) {
            resolve(false);
            return;
          }
          try {
            if (configuration?.scrollIntoViewOptions)
              actualHTMLElement?.scrollIntoView(
                configuration.scrollIntoViewOptions,
              );
          } catch (e) {
            console.error(e);
          }
          setTimeout(() => {
            if (!this.#checkInstruction(focusCheck)) {
              resolve(false);
            } else if (document.activeElement?.tagName === 'IFRAME') {
              resolve(
                (
                  document.activeElement as HTMLIFrameElement
                ).contentDocument?.contains(actualHTMLElement)
                  ? actualHTMLElement
                  : null,
              );
            } else
              resolve(
                document.activeElement === actualHTMLElement
                  ? actualHTMLElement
                  : null,
              );
          }, 0);
        }, 0);
      });
    }
    return actualHTMLElement;
  }

  #resetInterval() {
    clearTimeout(this.#focusTimeout);
    this.#focusTimeout = -1;
    this.#isIntervalRunning = false;
    this.#currentInstruction++;
  }

  async #runFocusInterval(
    focusElement?: IFocusQueryElement,
    internalCall?: boolean,
  ): Promise<false | HTMLElement> {
    if (!internalCall && (this.#focusTimeout !== -1 || this.#isIntervalRunning))
      return false;
    const currentInstruction: IFocusCheck = {
      currentInstruction: this.#currentInstruction,
    };
    this.#isIntervalRunning = true;
    return new Promise<false | HTMLElement>((resolve) => {
      const resolvePromise = async () => {
        const element = focusElement ?? this.#focusQuery.shift();

        if (element) {
          const hasFocused = await this.#doFocus(
            element.element,
            currentInstruction,
            element !== this.#focusQuery[0],
            element.configuration,
          );
          if (!this.#checkInstruction(currentInstruction)) {
            resolve(false);
            return;
          }
          if (hasFocused !== null && hasFocused !== false) {
            this.#actualFocusQuery = [];
            resolve(hasFocused);
            this.#isIntervalRunning = false;
            return;
          }
          if (hasFocused === false) {
            this.#actualFocusQuery = this.#actualFocusQuery.filter(
              (current) => current !== element,
            );
          }
        }

        if (!this.#checkInstruction(currentInstruction)) {
          resolve(false);
          return;
        }

        if (this.#actualFocusQuery.length > 0) {
          this.#focusTimeout = setTimeout(() => {
            const runInterval = async () => {
              const result = await this.#runFocusInterval(undefined, true);
              resolve(result);
              this.#isIntervalRunning = true;
            };
            void runInterval();
          }, this.#focusDelay) as unknown as number;
        } else {
          const lastFocused = globalFocus.last(':not(body)');
          if (lastFocused) {
            const result = await this.#doFocus(
              lastFocused,
              currentInstruction,
              element !== this.#focusQuery[0],
              element?.configuration,
            );
            if (!this.#checkInstruction(currentInstruction)) {
              resolve(false);
              return;
            }
            if (result) resolve(result);
            else resolve(false);
            this.#isIntervalRunning = true;
          }
          resolve(false);
          this.#isIntervalRunning = true;
        }
      };
      void resolvePromise();
    });
  }

  /**
   * Da la instrucción de colocar el foco en el elemento provisto como
   * parámetro una vez que todas las notificaciones se hayan cerrado. En caso
   * de no existir notificaciones abiertas, hace foco en el elemento
   * directamente.
   */
  afterNotifications(
    element: HTMLElement | TFocusRetriever,
    configuration?: IOnFocusConfiguration,
  ) {
    if (!element) return;
    void this.on(element, configuration);
  }

  /**
   * Da la instrucción de colocar el foco en el elemento provisto como
   * parámetro luego de recargar la página.
   *
   * Los parámetros pasados serán ordenados en orden de importancia priorizando
   * en el siguiente orden:
   * - id
   * - name
   * - className
   * - selector
   */
  afterReload({
    className,
    id,
    name,
    selector,
  }: TRequireOnlyOne<{
    className: string;
    id: string;
    name: string;
    selector: string;
  }>) {
    persistentStorage.focusOnReload = [
      id ? `#${id}` : undefined,
      name ? `[name="${name}"]` : undefined,
      className ? `.${className}` : undefined,
      selector,
    ]
      .filter((el) => el !== undefined)
      .join(',');
  }

  /**
   * Da la instrucción de colocar el foco el elemento pasado como parámetro.
   * Se puede pasar también una función que devuelva HTMLElement | false |
   * null.
   *
   * El segundo parámetro del método on es un objeto de tipo
   * ScrollIntoViewOptions, que permite modificar el comportamiento del scroll
   * a en el elemento.
   *
   * @see TFocusRetriever para más detalles sobre la función como parámetro.
   */
  async on(
    element: HTMLElement | TFocusRetriever,
    configuration?: IOnFocusConfiguration,
  ) {
    if (element) {
      const focusElement = {
        element,
        configuration,
      };
      for (let i = 0; i < this.#focusRetries; i++)
        this.#focusQuery.unshift(focusElement);

      this.#resetInterval();
      return this.#runFocusInterval(focusElement);
    }
    return false;
  }

  focusOnReload: string | null = null;

  restore() {
    void this.on(
      (globalFocus.last(':not(body), body#tinymce') as HTMLElement) ||
        (document.querySelector(focusSelector) as HTMLElement),
    );
  }

  init() {
    const modalsObserver = new MutationObserver((mutation) => {
      if (mutation[0].removedNodes.length === 0) return;
      this.afterNotifications(
        globalFocus.last(
          ':not(body):not(.notificationsView *), body#tinymce',
        ) as HTMLElement,
      );
    });
    const appModalsBox = (
      this.#props?.modalsContainerRetriever ??
      (() => this.#root.querySelector('#AppModals'))
    )();
    if (appModalsBox) modalsObserver.observe(appModalsBox, { childList: true });

    screenLocker.on('releaseForFirstTime', () => {
      if (persistentStorage.focusOnReload)
        this.focusOnReload = persistentStorage.focusOnReload as string;
      persistentStorage.focusOnReload = null;
      this.afterNotifications(() => {
        if (this.focusOnReload) {
          const initialFocusElement = document.querySelector(
            this.focusOnReload,
          );
          const scrollTop = noNaN(
            (window as unknown as Record<string, string>).CURRENT_TAB?.split(
              '~',
            )[1],
          );
          if (document.scrollingElement)
            document.scrollingElement.scrollTop = scrollTop;
          return initialFocusElement as HTMLElement | null;
        }
        return false;
      });
    });
  }
})(document.getElementById('root') as HTMLElement);
